import { Location, NgClass, AsyncPipe, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  signal,
  computed,
  inject,
  Signal
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatestWith, filter, Observable, Subscription, tap } from 'rxjs';
import { EnvironmentService } from '../../../../environments/environment.service';
import { LogService } from '../../../core/log/log.service';
import { MetaService } from '../../../core/meta/meta.service';
import { loadComments } from '../../../domain/content/content.actions';
import { Content, ContentVisualizationType } from '../../../domain/content/content.model';
import { selectCurrentContent } from '../../../domain/content/content.selectors';
import { getTemplateFromId, TemplateTypeId } from '../../../domain/template-ct/template-ct.model';
import { selectUserId } from '../../../domain/user/user.selectors';
import { loadLatestNews } from '../../../domain/widget/widget.actions';
import { selectWidgetLatestNewsContent } from '../../../domain/widget/widget.selectors';
import { ContentContainerComponent } from '../../content/content.component';
import { NotificationService } from '../../../core/notifications/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { SocialComponent } from '../../../shared/social/social.component';
import { DnPdfViewerComponent } from '../../../shared/pdf-viewer/pdf-viewer.component';
import { ContentListViewerComponent } from '../../../shared/content-list-viewer/content-list-viewer.component';
import { MessageInPageComponent } from '../../../shared/message-in-page/message-in-page.component';
import { ContentViewComponent } from '../../../shared/content-view/content-view.component';
import { PollsComponent } from '../../../shared/polls/polls.component';
import { PollSmartComponent } from '../../poll-smart/poll-smart.component';
import { Poll, PollQuestionAnswer } from '../../../domain/poll-smart/poll-smart.model';
import { selectPoll } from '../../../domain/poll-smart/poll-smart.selectors';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'dottnet-file',
  templateUrl: './file.component.html',
  styleUrls: ['./file.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, ContentViewComponent, MessageInPageComponent, ContentListViewerComponent, DnPdfViewerComponent, SocialComponent, DatePipe, PollsComponent],
  providers: [PollSmartComponent]
})
export class FileComponent extends ContentContainerComponent implements OnInit, OnDestroy {
  //  Reference to access the enum in the template
  readonly FileVisualizationType = ContentVisualizationType;
  readonly TemplateTypeId = TemplateTypeId;

  // Conversione da Observable a Signal
  articleLatestNews = signal<Content[]>([]);

  poll$: Observable<Poll> = new Observable();
  readonly pollSignal = signal<Poll | null>(null);
  pollId: number;

  authSub: Subscription;

  constructor(
    store: Store,
    actRoute: ActivatedRoute,
    envService: EnvironmentService,
    router: Router,
    logService: LogService,
    private changeDetectorRef: ChangeDetectorRef,
    location: Location,
    notificationService: NotificationService,
    metaService: MetaService,
    translate: TranslateService,
    changeDetector: ChangeDetectorRef,
    private pollClass: PollSmartComponent
  ) {
    super(
      store,
      actRoute,
      envService,
      router,
      logService,
      location,
      TemplateTypeId.FILE,
      notificationService,
      metaService,
      translate,
      changeDetector
    );
  }

  ngOnInit(): void {
    super.init();

    this.poll$ = this.store.select(selectPoll);

    // Sottoscrizione per aggiornare il signal con i dati delle ultime news
    this.subList.push(
      this.store.select(selectWidgetLatestNewsContent).subscribe(news => {
        this.articleLatestNews.set(news);
      })
    );

    this.subList.push(
      this.contentError$
        .pipe(tap((error) => this.logService.info('content error article: ', error)))
        .subscribe((error) => {
          this.contentError = error;
          this.changeDetectorRef.detectChanges();
        })
    );

    // mi sottoscrivo all'authState  per poter dispatchare le get dei widget
    // vado in combineLatestWith l'observable di template per poter utlizzare i parametri utili ai dispatch
    this.subList.push(
      this.store
        .select(selectUserId)
        .pipe(
          filter((userId) => !!userId),
          combineLatestWith(
            this.store
              .select(selectCurrentContent)
              .pipe(filter((currentContent) => !!currentContent))
          )
        )
        .subscribe(([userId, currentContent]) => {

          this.userId = userId;
          this.pollId = currentContent.idPoll;
          // se esiste un poll associato, ne carico i dettagli
          if (this.pollId > 0) {
            this.pollClass.getPoll(this.pollId, this.userId);
          }
          // Comments
          const payloadLoadComments = {
            commentId: currentContent.idContenuto,
            userId: userId,
            template: getTemplateFromId(TemplateTypeId.FILE)
          };
          this.store.dispatch(loadComments(payloadLoadComments));
        })
    );

    this.subList.push(
      this.store
        .select(selectUserId)
        .pipe(filter((userId) => !!userId))
        .subscribe((userId) => {
          // LatestNews
          this.store.dispatch(
            loadLatestNews({
              userId: userId,
              fromWhere: 0,
              howMany: 4
            })
          );
        })
    );

    // Subscribe to poll$ to update pollSignal
    this.subList.push(
      this.poll$.subscribe(poll => {
        if (poll) {
          this.pollSignal.set(poll);
        }
      })
    );
  }

  submitPoll(PollQuestionAnswer: PollQuestionAnswer[]): void {
    this.pollClass.submitPoll(PollQuestionAnswer, this.userId, this.pollId);
  }

  undoPoll(): void {
    this.pollClass.undoPoll(this.userId, this.pollId);
  }

  ngOnDestroy(): void {
    super.destroy();
  }
}
